import React, { useEffect, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

/**
 * Read-only xterm preview that types a scripted CTF demo. Used on /pricing
 * to showcase the real Pro terminal without opening a WebSocket.
 */
const DEMO_SCRIPT = [
  { delay: 600, text: '\x1b[0;32muser@cyberinsti\x1b[0m:\x1b[0;34m~\x1b[0m$ ' },
  { delay: 400, type: 'whoami\r\n' },
  { delay: 250, text: 'operator\r\n' },
  { delay: 300, text: '\x1b[0;32muser@cyberinsti\x1b[0m:\x1b[0;34m~\x1b[0m$ ' },
  { delay: 300, type: 'ls -la /challenge\r\n' },
  { delay: 250, text: 'total 24\r\n' },
  { delay: 30,  text: 'drwxr-xr-x 2 nobody nogroup 4096 Apr 22 12:01 .\r\n' },
  { delay: 30,  text: '-rw-r--r-- 1 nobody nogroup  742 Apr 22 12:01 access.log\r\n' },
  { delay: 30,  text: '-rw------- 1 nobody nogroup  128 Apr 22 12:01 .flag\r\n' },
  { delay: 30,  text: '-rw-r--r-- 1 nobody nogroup  201 Apr 22 12:01 notes.md\r\n' },
  { delay: 500, text: '\x1b[0;32muser@cyberinsti\x1b[0m:\x1b[0;34m~\x1b[0m$ ' },
  { delay: 300, type: 'grep -Ei "flag|admin" /challenge/access.log\r\n' },
  { delay: 250, text: '\x1b[33m192.168.1.4 - admin [22/Apr:11:05] "POST /login" 200\x1b[0m\r\n' },
  { delay: 30,  text: '\x1b[33m10.0.0.42  - root  [22/Apr:11:07] "GET /flag.txt" 200 42\x1b[0m\r\n' },
  { delay: 500, text: '\x1b[0;32muser@cyberinsti\x1b[0m:\x1b[0;34m~\x1b[0m$ ' },
  { delay: 300, type: 'cat .flag\r\n' },
  { delay: 300, text: '\x1b[32mflag{real_pty_real_ctf_real_fast}\x1b[0m\r\n' },
  { delay: 300, text: '\x1b[0;32muser@cyberinsti\x1b[0m:\x1b[0;34m~\x1b[0m$ \x1b[90m# ← your turn, on Pro.\x1b[0m' },
];

const THEME = {
  background: '#0A0A0A',
  foreground: '#E5E5E5',
  cursor: '#00FF00',
  cursorAccent: '#0A0A0A',
  selectionBackground: '#00FF0040',
  green: '#00FF00',
  yellow: '#FFD700',
  blue: '#00BFFF',
  red: '#FF5555',
  white: '#E5E5E5',
};

const PricingTerminalDemo = ({ height = 260 }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const term = new Terminal({
      theme: THEME,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: 12,
      cursorBlink: true,
      disableStdin: true,
      convertEol: true,
      scrollback: 100,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);

    let isReady = false;
    const safeFit = () => {
      if (!isReady) return;
      try {
        // eslint-disable-next-line no-underscore-dangle
        if (!term._core?._renderService?.dimensions?.css?.cell?.width) return;
        fit.fit();
      } catch (err) {
        console.debug('[pricing-demo] xterm fit skipped:', err?.message);
      }
    };
    const renderDisposer = term.onRender(() => {
      if (!isReady) {
        isReady = true;
        setTimeout(safeFit, 0);
      }
    });

    let ro;
    try {
      ro = new ResizeObserver(safeFit);
      ro.observe(containerRef.current);
    } catch (err) {
      console.debug('[pricing-demo] ResizeObserver unavailable:', err?.message);
    }

    let cancelled = false;
    let timers = [];

    const writeChar = (str, i, done) => {
      if (cancelled) return;
      if (i >= str.length) {
        done();
        return;
      }
      term.write(str[i]);
      timers.push(setTimeout(() => writeChar(str, i + 1, done), 45));
    };

    const runScript = (idx) => {
      if (cancelled) return;
      if (idx >= DEMO_SCRIPT.length) {
        // Loop the demo after a pause for eternal autoplay
        timers.push(
          setTimeout(() => {
            if (!cancelled) {
              term.clear();
              runScript(0);
            }
          }, 4000),
        );
        return;
      }
      const step = DEMO_SCRIPT[idx];
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          if (step.type) {
            writeChar(step.type, 0, () => runScript(idx + 1));
          } else {
            term.write(step.text);
            runScript(idx + 1);
          }
        }, step.delay || 100),
      );
    };

    // Kick things off after fit is likely ready
    timers.push(setTimeout(() => runScript(0), 400));

    return () => {
      cancelled = true;
      timers.forEach((t) => clearTimeout(t));
      renderDisposer.dispose();
      if (ro) {
        try {
          ro.disconnect();
        } catch (err) {
          console.debug('[pricing-demo] RO disconnect threw:', err?.message);
        }
      }
      term.dispose();
    };
  }, []);

  return (
    <div
      className="border border-gray-800 rounded-xl overflow-hidden"
      data-testid="pricing-terminal-demo"
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-[#0F0F0F] border-b border-gray-800">
        <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
        <span className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
        <span className="w-3 h-3 rounded-full bg-[#27C93F]" />
        <span className="ml-3 text-xs text-gray-500 font-mono">
          live preview · real bash shell on Pro
        </span>
      </div>
      <div
        ref={containerRef}
        style={{ height, backgroundColor: '#0A0A0A', padding: '8px' }}
      />
    </div>
  );
};

export default PricingTerminalDemo;
